import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getMapObjects,
  MapObject,
} from "../models/mapObject";
import type {
  HouseFurniture,
  FurnitureCatalog,
} from "../models/apiTypes";
import {
  getFurnitureCatalogFurnitureCatalogGet,
  placeFurnitureInteriorObjectsInteriorObjectIdFurnitureFurnitureIdPlacePost,
} from "../client";
import { fetchHouseFurniture } from "../scenes/room/state/layoutSync";
import { FURNITURE_CATEGORIES, FURNITURE_CATEGORY_NAMES, getFurnitureThumbPath, getFurnitureDescription, getFurnitureName, isWallMountedFurniture } from "../data/furniture";
import { useUserData } from "../hooks/useUserData";
import { impactOccurred } from "../utils/telegram/hapticFeedback";
import { createRetryFunction } from "../utils/net/apiError";
import ScreenHeaderWithTabs from "../components/ScreenHeaderWithTabs";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import useInterfaceStore from "../stores/useInterfaceStore";
import { EmptyState, ScreenError, ScreenLoading } from "../ui/primitives/ScreenStatus";
import GameScreenFrame from "../ui/primitives/GameScreenFrame";
import { cn } from "../ui/cn";


type CategoryType = "all" | typeof FURNITURE_CATEGORIES[number];

interface HouseFurnitureInventoryScreenProps {
  mapObjectId: number;
  /** Back to the room under it (the inventory is a panel over the room, not a route). */
  onClose: () => void;
  roomIndex?: number;
  onFurniturePlaced?: (furniture: HouseFurniture) => void;
}

export default function HouseFurnitureInventoryScreen({ 
  mapObjectId, 
  onClose,
  roomIndex = 0,
  onFurniturePlaced 
}: HouseFurnitureInventoryScreenProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<CategoryType>("all");
  const [placingFurnitureId, setPlacingFurnitureId] = useState<number | null>(null);
  const setJustPlacedFurnitureCatalogId = useInterfaceStore((s) => s.setJustPlacedFurnitureCatalogId);
  const { data: userData } = useUserData();
  const isAdmin = userData?.is_admin ?? false;

  // Кнопка "Назад" (Telegram або fallback)
  const handleBack = onClose;

  useTelegramBackButton(true, handleBack);

  // Завантажуємо інвентар будинку
  const { data: houseFurniture, error, isLoading, refetch } = useQuery<HouseFurniture[]>({
    queryKey: ["houseFurniture", mapObjectId],
    queryFn: () => fetchHouseFurniture(mapObjectId),
    retry: createRetryFunction(1),
    enabled: true,
  });

  // Furniture catalog query (fallback to fill missing furniture_catalog)
  const { data: furnitureCatalog = [] } = useQuery({
    queryKey: ["furnitureCatalog"],
    queryFn: async () => {
      const { data } = await getFurnitureCatalogFurnitureCatalogGet({ throwOnError: true });
      return data.items;
    },
    enabled: true,
  });

  // Отримуємо mapObjects для визначення типу об'єкта (мерія чи будинок)
  const { data: mapObjects = [] } = useQuery<MapObject[]>({
    queryKey: ["mapObjects"],
    queryFn: () => getMapObjects(),
    enabled: true,
  });

  // Визначаємо, чи це мерія
  const isCityHall = useMemo(() => {
    const mapObject = mapObjects.find(obj => obj.id === mapObjectId);
    return mapObject?.type === "cityhall";
  }, [mapObjects, mapObjectId]);

  // Backend now sends furniture_catalog directly; fallback enrichment for edge cases
  const enrichedHouseFurniture = useMemo(() => {
    if (!houseFurniture) return [];

    return houseFurniture.map(f => {
      if (f.furniture_catalog) return f;

      const catalogItem = furnitureCatalog.find((c: FurnitureCatalog) => c.id === f.furniture_catalog_id);
      if (catalogItem) {
        return { ...f, furniture_catalog: catalogItem };
      }

      return f;
    });
  }, [houseFurniture, furnitureCatalog]);

  // Фільтрація меблів по категорії
  const filteredItems = useMemo(() => {
    if (!enrichedHouseFurniture) return [];
    
    // Filter out items without furniture_catalog
    const validItems = enrichedHouseFurniture.filter(item => item.furniture_catalog);
    
    let items = selectedCategory === "all" 
      ? validItems 
      : validItems.filter(item => item.furniture_catalog!.category === selectedCategory);
    
    // Сортуємо: спочатку невстановлені, потім встановлені
    items = [...items].sort((a, b) => {
      if (a.is_placed === b.is_placed) return 0;
      return a.is_placed ? 1 : -1;
    });
    
    return items;
  }, [enrichedHouseFurniture, selectedCategory]);

  // Get all unique categories from inventory
  const availableCategories = useMemo(() => {
    if (!enrichedHouseFurniture) return [];
    const categories = new Set<string>();
    enrichedHouseFurniture.forEach(item => {
      if (item.furniture_catalog?.category) {
        categories.add(item.furniture_catalog.category);
      }
    });
    return Array.from(categories).sort();
  }, [enrichedHouseFurniture]);

  // Get translated category names map
  const categoryNamesMap = useMemo(() => FURNITURE_CATEGORY_NAMES(t), [t]);

  const handlePlaceFurniture = async (furniture: HouseFurniture) => {
    if (!furniture.furniture_catalog) {
      return;
    }
    
    if (furniture.is_placed) {
      return;
    }

    setPlacingFurnitureId(furniture.id);
    impactOccurred('light');

    try {
      // Place furniture in center of room (will be adjusted by RoomCanvas)
      // Room center coordinates (0, 250) in room-local coords
      const centerX = 0;
      const centerY = 250;
      
      const { data: placed } = await placeFurnitureInteriorObjectsInteriorObjectIdFurnitureFurnitureIdPlacePost({
        path: {
          interior_object_id: mapObjectId,
          furniture_id: furniture.id,
        },
        body: {
          room_index: roomIndex,
          x: centerX,
          y: centerY,
        },
        throwOnError: true,
      });

      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: ["houseFurniture", mapObjectId] });
      
      // Notify parent component
      if (onFurniturePlaced) {
        onFurniturePlaced(placed);
      }
      
      // Не блокуємо перехід у кімнату очікуванням refetch:
      // в RoomCanvas дані підтягнуться через invalidateQueries.
      void refetch();

      // Back to the room under the panel right after Place.
      setJustPlacedFurnitureCatalogId(furniture.furniture_catalog_id);
      onClose();
      
    } catch (error: unknown) {
      console.error("Place furniture error:", error);
    } finally {
      setPlacingFurnitureId(null);
    }
  };

  if (isLoading) {
    return (
      <ScreenLoading label={t("Loading...")} />
    );
  }

  if (error) {
    return (
      <ScreenError
        title={t("Error loading furniture inventory")}
        detail={error instanceof Error ? error.message : String(error)}
      />
    );
  }

  return (
    <>
    <GameScreenFrame showNavigation={false}>
        <ScreenHeaderWithTabs
          title={t("My furniture")}
          tabs={[]}
          selectedTab=""
          onTabChange={() => {}}
          scrollable={false}
        />

        {/* Кнопка "Назад" (fallback якщо Telegram back button не видима) */}
        {!window.Telegram?.WebApp?.BackButton?.isVisible && (
          <button
            onClick={handleBack}
            className="absolute top-xl left-xl z-[1000] flex cursor-pointer items-center gap-sm rounded-md border border-solid border-white-alpha-30 bg-black-alpha-60 px-xl py-md text-[14px] font-semibold text-white"
          >
            ← {t("Back")}
          </button>
        )}

        {/* Scrollable category pills */}
        <div className="bg-fullscreen-surface px-2xl pt-xl pb-md">
          <div className="flex flex-nowrap items-center gap-md overflow-x-scroll overflow-y-hidden pb-sm [direction:ltr] [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:var(--color-brand-alpha-85)_var(--color-white-alpha-12)]">
            {/* "All" category pill */}
            <button
              onClick={() => setSelectedCategory("all")}
              className={cn(
                "shrink-0 cursor-pointer rounded-[20px] border-none px-xl py-md [font-family:Lato,sans-serif] text-[14px] font-bold whitespace-nowrap text-white",
                selectedCategory === "all"
                  ? "bg-brand-alpha-85"
                  : "bg-white-alpha-12",
              )}
            >
              {t("All furniture")}
            </button>

            {/* Category pills */}
            {availableCategories.map((category) => {
              const isSelected = selectedCategory === category;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category as CategoryType)}
                  className={cn(
                    "shrink-0 cursor-pointer rounded-[20px] border-none px-xl py-md [font-family:Lato,sans-serif] text-[14px] font-bold whitespace-nowrap text-white",
                    isSelected
                      ? "bg-brand-alpha-85"
                      : "bg-white-alpha-12",
                  )}
                >
                  {categoryNamesMap[category as keyof typeof categoryNamesMap] || category}
                  {/* Extract category keys for i18next-parser (static t() calls, never rendered) */}
                  {/* eslint-disable-next-line no-constant-binary-expression -- dead branch keeps keys visible to i18next-parser */}
                  {false && (
                    <>
                      {t("Beds")}
                      {t("Rugs")}
                      {t("Nightstands")}
                      {t("Storage")}
                      {t("Windows")}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Info block about house-bound furniture */}
        <div className="bg-fullscreen-surface px-2xl pt-0 pb-xl">
          <div className="box-border flex w-full flex-col items-center gap-lg rounded-xl bg-white-alpha-8 px-3xl py-xl text-center">
            <div className="text-[14px] leading-[1.4] text-white-alpha-70">
              {isCityHall 
                ? "Ці меблі можна встановити лише в офісі мерії. Меблі назавжди залишаються в мерії і передаються наступним мерам."
                : t("These furniture items are bound only to this house.")
              }
            </div>
          </div>
        </div>

        {/* Контент з меблями */}
        <div className="box-border flex min-h-0 flex-1 flex-col items-center gap-3xl px-2xl pt-3xl pb-[var(--bottom-nav-height)]">
          {!filteredItems || filteredItems.length === 0 ? (
            <EmptyState>
              {t("No furniture in inventory")}
            </EmptyState>
          ) : (
            filteredItems
              .filter(furniture => furniture.furniture_catalog) // Filter out items without catalog
              .map((furniture) => {
              const isLoading = placingFurnitureId === furniture.id;
              const isPlaced = furniture.is_placed;

              return (
                <article
                  key={furniture.id}
                  className={cn(
                    "box-border flex w-full max-w-[360px] flex-col gap-lg rounded-xl px-2xl py-[13px]",
                    isPlaced
                      ? "border-2 border-solid border-brand-alpha-80 bg-[linear-gradient(135deg,var(--color-brand-alpha-20)_0%,var(--color-brand-alpha-10)_100%)]"
                      : "border-2 border-solid border-white-alpha-30 bg-[linear-gradient(135deg,var(--color-white-alpha-10)_0%,var(--color-white-alpha-5)_100%)]",
                  )}
                >
                  {/* Верхня частина: зображення та статус */}
                  <div className="flex min-w-0 items-center justify-between gap-md">
                    {/* Зображення меблів */}
                    <div className="relative h-[143px] w-[143px] shrink-0">
                      <div className="absolute inset-0 h-[143px] w-[143px] aspect-square rounded-[18px] bg-[image:var(--gradient-item-spotlight)] blur-[11.75px]" />
                      <img
                        src={getFurnitureThumbPath(furniture.furniture_catalog)}
                        loading="lazy"
                        decoding="async"
                        alt={getFurnitureName(furniture.furniture_catalog.id, t)}
                        className={cn(
                          "relative m-[15.89px] h-[111.231px] w-[111.231px] rounded-[18px] object-contain",
                          isPlaced ? "opacity-70" : "opacity-100",
                        )}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      {isAdmin && !isWallMountedFurniture(furniture.furniture_catalog) && furniture.furniture_catalog?.category !== 'rugs' && (
                        <div className="absolute top-xs right-xs flex flex-col items-end gap-[2px]">
                          {furniture.furniture_catalog?.footprint_tiles === null && (
                            <span className="rounded-[6px] bg-fitness-penalty-alpha-90 px-[5px] py-px text-[11px] leading-[1.4] font-bold text-white">−fp</span>
                          )}
                          {furniture.furniture_catalog?.surface_tiles === null && (
                            <span className="rounded-[6px] bg-surface-penalty-alpha-90 px-[5px] py-px text-[11px] leading-[1.4] font-bold text-white">−sf</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Статус та кнопка */}
                    <div className="flex min-w-0 max-w-full flex-1 flex-col items-center gap-md">
                      {isPlaced ? (
                        <div className="rounded-pill bg-brand-alpha-30 px-xl py-md text-center [font-family:Lato,sans-serif] text-[14px] font-bold text-white">
                          {t("Placed")}
                        </div>
                      ) : (
                        <button
                          onClick={() => handlePlaceFurniture(furniture)}
                          disabled={isLoading}
                          className={cn(
                            "box-border inline-flex max-w-full items-center justify-center gap-sm rounded-pill border-none bg-white px-md py-sm text-center [font-family:'Rubik_One',sans-serif] text-[13px] leading-[18px] font-normal tracking-[-0.67px] break-words whitespace-normal text-text-dark",
                            isLoading ? "cursor-not-allowed opacity-60" : "cursor-pointer opacity-100",
                          )}
                        >
                          {isLoading ? t("Installing...") : t("Select") + " ✅"}
                          {/* Extract keys for i18next-parser (static t() calls, never rendered) */}
                          {/* eslint-disable-next-line no-constant-binary-expression -- dead branch keeps keys visible to i18next-parser */}
                          {false && (
                            <>
                              {t("Installing...")}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Назва */}
                  <div className="flex flex-col gap-md">
                    <h3 className="m-0 [font-family:'Rubik_One',sans-serif] text-[18px] leading-[30px] font-normal text-white">
                      {getFurnitureName(furniture.furniture_catalog.id, t)}
                    </h3>
                    {getFurnitureDescription(furniture.furniture_catalog.id, t) && (
                      <p className="mt-sm mb-0 self-stretch [font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-white not-italic">
                        {getFurnitureDescription(furniture.furniture_catalog.id, t)}
                      </p>
                    )}
                    {isPlaced && furniture.x !== null && furniture.y !== null && (
                      <p className="m-0 mt-md [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-light tracking-[-0.747px] text-white-alpha-70">
                        {t("Room")}: {furniture.room_index + 1}
                      </p>
                    )}
                  </div>
                </article>
              );
            })
          )}
            {/* Фіксований буфер */}
            <div className="h-[var(--bottom-nav-height)] min-h-[var(--bottom-nav-height)] w-full shrink-0" />
        </div>
    </GameScreenFrame>
    </>
  );
}
